import { useEffect, useState } from "react";
import Button from "../../components/Button";
import ProcessMedicationChangeRequestCard from "./ProcessMedicationChangeRequestCard";
import { trimObjectValues } from "../../utils/form";
import type { MedicationFormValue } from "./MedicationItemForm";

export interface MedicationReviewRequestItem {
    id: number;
    name: string;
    quantity: number;
    unit?: string | null;
    unit_display?: string | null;
    stock_quantity?: number | null;
    note?: string | null;
    price?: number | null;
    stock_status?: string | null;
}

export interface MedicationChangeRequest {
    id?: number;
    prescription_number?: string | null;
    vn?: string | null;
    patient_name?: string | null;
    order_time_display?: string | null;
    medication_review_request_items?: MedicationReviewRequestItem[];
}

interface ModalFormValue extends MedicationFormValue {
    medication_review_request_item_id: number;
}

interface ProcessMedicationChangeRequestModalProps {
    isOpen: boolean;
    onClose: () => void;
    requestChange: Partial<MedicationChangeRequest>;
    onSubmit: (payload: Record<string, unknown>) => void;
}

const EMPTY_MEDICATION_REVIEW_REQUEST_ITEMS: MedicationReviewRequestItem[] = [];

/*** 
* ProcessMedicationChangeRequestModal
* 
* A modal component for processing medication change requests.
*
* @param {object} props
* @param {boolean} props.isOpen
*   Indicate whether the modal is open.
* @param {function} props.onClose
*  Function to call when closing the modal.
* @param {object} props.requestChange
*   The medication change request data object.
* @param {function} props.onSubmit
*  Function to call when submitting the processed medication change request.
*/
export default function ProcessMedicationChangeRequestModal({
    isOpen,
    onClose,
    requestChange = {},
    onSubmit,
}: ProcessMedicationChangeRequestModalProps) {
    const [formValues, setFormValues] = useState<ModalFormValue[]>([]);
    const medicationReviewRequestItems = Array.isArray(requestChange?.medication_review_request_items)
        ? requestChange.medication_review_request_items
        : EMPTY_MEDICATION_REVIEW_REQUEST_ITEMS;

    useEffect(() => {
        const nextFormValues = medicationReviewRequestItems.map(item => ({
                medication_review_request_item_id: item.id,
                action: '',
                quantity: '',
                note: '',
                medication: null,
                medication_id: null,
                unit: '',
                usage_instruction: '',
            }));

        setFormValues((previousFormValues) => {
            const hasSameItems = previousFormValues.length === nextFormValues.length &&
                previousFormValues.every((item, index) =>
                    item.medication_review_request_item_id === nextFormValues[index].medication_review_request_item_id
                );
            return hasSameItems ? previousFormValues : nextFormValues;
        });
    }, [medicationReviewRequestItems]);

    if (!isOpen) return null;

    const handleMedicationChange = (itemId: number, updatedFormValue: MedicationFormValue) => {
        setFormValues(prev =>
            prev.map(item =>
                item.medication_review_request_item_id === itemId ? { ...item, ...updatedFormValue } : item
            )
        );
    };

    const handleSubmit = () => {
        const filteredFormValues = formValues
            .filter(value => value.action)
            .map(value => {
                const item: Record<string, unknown> = {
                    medication_review_request_item_id: value.medication_review_request_item_id,
                    action: value.action,
                };
                if (value.action === 'adjust' && value.quantity) {
                    item.new_quantity = parseInt(String(value.quantity), 10);
                }
                if (value.action === 'change') {
                    if (value.medication_id) {
                        item.new_medication_id = value.medication_id;
                    }
                    if (value.quantity) {
                        item.new_quantity = parseInt(String(value.quantity), 10);
                    }
                    if (value.unit) {
                        item.new_unit = value.unit;
                    }
                    if (value.usage_instructions) {
                        item.new_usage_instruction = value.usage_instructions;
                    }
                }
                if (value.note) {
                    item.note = value.note;
                }
                return item;
            });

        const payload = { medication_review_request_items: filteredFormValues };
        const cleanedPayload = trimObjectValues(payload);
        onSubmit(cleanedPayload);
    };

    function buildPrescriptionDetails() {
        const prescriptionDetails = [
            { label: "เลขที่ใบสั่งยา", value: requestChange.prescription_number },
            { label: "VN", value: requestChange.vn },
            { label: "ผู้ป่วย", value: requestChange.patient_name },
            { label: "เวลาสั่ง", value: requestChange.order_time_display },
        ];

        return (
            <div className="flex flex-1 items-start gap-5">
                {prescriptionDetails.map((detail) => (
                    <div key={detail.label} className="flex flex-1 flex-col gap-0.5">
                        <div className="text-body-small text-main-typography-system-description">{detail.label}</div>
                        <div className="font-semibold text-body-base text-main-typography-system-default">{detail.value || "-"}</div>
                    </div>
                ))}
            </div>
        );
    }

    function buildMedicationItems() {
        return (
            <div className="flex flex-col h-[360px] rounded-lg p-5 gap-3 bg-main-background-system-default-dim overflow-y-auto custom-scrollbar">
                {medicationReviewRequestItems.map((medication, index) => (
                    <ProcessMedicationChangeRequestCard
                        key={medication.id}
                        medication={medication}
                    formValue={formValues[index] || {
                        medication_review_request_item_id: medication.id,
                        quantity: "",
                        unit: "",
                    }}
                        onChange={(updatedFormValue) => handleMedicationChange(medication.id, updatedFormValue)}
                    />
                ))}
            </div>
        );
    }

    function buildFooter() {
        // Disable submit if all form values are empty
        const hasSomeEmpty = formValues.some(
            (formValue) => !formValue.action
        );

        // Disable submit if any medication item has new quantity or note filled but no action selected
        const isInvalidEntry = formValues.some(
            (formValue) => (formValue.quantity || formValue.note) && !formValue.action
        );

        // Disable submit if action is "adjust" but new quantity is not filled
        const isAdjustWithoutQuantity = formValues.some(
            (formValue) => formValue.action === "adjust" && !formValue.quantity
        );

        // Disable submit if action is "change" but required fields are not filled
        const isChangeIncomplete = formValues.some(
            (formValue) =>
                formValue.action === "change" &&
                (!formValue.medication_id ||
                    !formValue.quantity ||
                    !formValue.unit ||
                    !formValue.usage_instructions)
        );

        const disabled = isInvalidEntry || hasSomeEmpty || isAdjustWithoutQuantity || isChangeIncomplete;
        return (
            <div className="flex justify-end gap-3">
                <Button hierarchy="secondary" size="md" text="ย้อนกลับ" className="w-[120px]" onClick={onClose} />
                <Button hierarchy="primary" size="md" text="บันทึก" className="w-[120px]" onClick={handleSubmit} disabled={disabled} />
            </div>);

    }

    return (
        <div className="modal-overlay custom-scrollbar">
            <div className="modal-content w-[954px]">
                <div className="flex flex-col gap-3">
                    <h2 className="font-semibold text-title text-main-typography-system-default">คำร้องแก้ไขยา</h2>
                    {buildPrescriptionDetails()}
                </div>
                {buildMedicationItems()}
                <hr className="border-t border-main-border-system-default w-full" />
                {buildFooter()}
            </div>
        </div>
    );
}
